const CART_UPDATE_EVENT = 'cart:update';
const ADD_TO_CART_ANIMATION_MS = 2000;
const TAB_FADE_DURATION_MS = 250;
const UPSELL_SCROLL_RATIO = 0.85;

/**
 * @param {HTMLElement} carousel
 */
const updateUpsellCarouselControls = (carousel) => {
  const scroller = carousel.querySelector('.cart-modal__upsell-products');
  const prevButton = carousel.querySelector('[data-cart-upsell-scroll="prev"]');
  const nextButton = carousel.querySelector('[data-cart-upsell-scroll="next"]');

  if (!(scroller instanceof HTMLElement) || !(prevButton instanceof HTMLButtonElement) || !(nextButton instanceof HTMLButtonElement)) {
    return;
  }

  const canScroll = scroller.scrollWidth > scroller.clientWidth + 1;
  carousel.classList.toggle('cart-modal__upsell-carousel--scrollable', canScroll);

  if (!canScroll) {
    prevButton.hidden = true;
    nextButton.hidden = true;
    return;
  }

  const atStart = scroller.scrollLeft <= 1;
  const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;

  prevButton.hidden = atStart;
  nextButton.hidden = atEnd;
};

/**
 * @param {HTMLElement} carousel
 * @param {'prev' | 'next'} direction
 */
const scrollUpsellCarousel = (carousel, direction) => {
  const scroller = carousel.querySelector('.cart-modal__upsell-products');
  if (!(scroller instanceof HTMLElement)) return;

  const distance = scroller.clientWidth * UPSELL_SCROLL_RATIO;
  scroller.scrollBy({
    left: direction === 'next' ? distance : -distance,
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });
};

/**
 * @param {ParentNode} root
 */
const initCartUpsellCarousels = (root = document) => {
  const carousels = root.querySelectorAll('[data-cart-upsell-carousel]');

  for (const carousel of carousels) {
    if (!(carousel instanceof HTMLElement)) continue;

    const scroller = carousel.querySelector('.cart-modal__upsell-products');
    if (!(scroller instanceof HTMLElement)) continue;

    if (carousel.dataset.upsellCarouselInit !== 'true') {
      carousel.dataset.upsellCarouselInit = 'true';
      scroller.addEventListener('scroll', () => updateUpsellCarouselControls(carousel), { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        const resizeObserver = new ResizeObserver(() => updateUpsellCarouselControls(carousel));
        resizeObserver.observe(scroller);
      }
    }

    updateUpsellCarouselControls(carousel);
  }
};

/**
 * @returns {boolean}
 */
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!window.__cartUpsellCarouselClickBound) {
  window.__cartUpsellCarouselClickBound = true;

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const button = event.target.closest('[data-cart-upsell-scroll]');
      if (!(button instanceof HTMLButtonElement)) return;

      const carousel = button.closest('[data-cart-upsell-carousel]');
      if (!(carousel instanceof HTMLElement)) return;

      const direction = button.dataset.cartUpsellScroll === 'prev' ? 'prev' : 'next';
      event.preventDefault();
      scrollUpsellCarousel(carousel, direction);
    },
    true
  );
}

window.initCartUpsellCarousels = initCartUpsellCarousels;

/**
 * @param {HTMLElement} root
 * @returns {HTMLElement[]}
 */
const getUpsellTabs = (root) =>
  Array.from(root.querySelectorAll('[data-cart-modal-tabs] .cart-modal__tab')).filter(
    (el) => el instanceof HTMLElement
  );

/**
 * @param {HTMLElement} root
 * @returns {HTMLElement[]}
 */
const getUpsellPanels = (root) =>
  Array.from(root.querySelectorAll('[data-cart-modal-tabs] [role="tabpanel"]')).filter(
    (el) => el instanceof HTMLElement
  );

/**
 * @param {HTMLElement} root
 * @param {HTMLElement} tab
 */
const updateUpsellTabIndicator = (root, tab) => {
  const nav = root.querySelector('.cart-modal__tablist-wrapper');
  const indicator = root.querySelector('.cart-modal__tab-indicator');
  if (!(nav instanceof HTMLElement) || !(indicator instanceof HTMLElement)) return;

  const navRect = nav.getBoundingClientRect();
  const tabRect = tab.getBoundingClientRect();
  indicator.style.left = `${tabRect.left - navRect.left + nav.scrollLeft}px`;
  indicator.style.width = `${tabRect.width}px`;
};

/**
 * @param {HTMLElement} root
 * @param {HTMLElement} tabEl
 * @param {boolean} animate
 */
const switchUpsellTab = (root, tabEl, animate = true) => {
  if (tabEl.getAttribute('aria-selected') === 'true') return;

  const tabs = getUpsellTabs(root);
  const panels = getUpsellPanels(root);
  const controlsId = tabEl.getAttribute('aria-controls');
  if (!controlsId || !tabs.length || !panels.length) return;

  const currentTab = tabs.find((t) => t.getAttribute('aria-selected') === 'true');
  const currentPanel = currentTab
    ? panels.find((p) => p.id === currentTab.getAttribute('aria-controls'))
    : null;
  const nextPanel = panels.find((p) => p.id === controlsId);
  const currentContent = currentPanel?.querySelector('.cart-modal__tabpanel-content');
  const nextContent = nextPanel?.querySelector('.cart-modal__tabpanel-content');

  const finishSwitch = () => {
    for (const panel of panels) {
      const isTarget = panel.id === controlsId;
      const panelContent = panel.querySelector('.cart-modal__tabpanel-content');

      if (isTarget) {
        panel.removeAttribute('hidden');
        if (panelContent instanceof HTMLElement) {
          panelContent.classList.remove('cart-modal__tabpanel-content--fade-out');
          panelContent.style.opacity = '1';
        }
      } else {
        panel.setAttribute('hidden', 'true');
      }
    }

    for (const tab of tabs) {
      const isActive = tab === tabEl;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.classList.toggle('cart-modal__tab--selected', isActive);
    }

    requestAnimationFrame(() => {
      updateUpsellTabIndicator(root, tabEl);
      initCartUpsellCarousels(root);
    });
  };

  const fadeInNewPanel = () => {
    if (!(nextContent instanceof HTMLElement)) return;
    nextContent.classList.remove('cart-modal__tabpanel-content--fade-out');
    nextContent.style.opacity = '0';
    nextContent.offsetHeight;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        nextContent.style.opacity = '1';
      });
    });
  };

  if (!animate || !(nextPanel instanceof HTMLElement) || !(nextContent instanceof HTMLElement)) {
    finishSwitch();
    return;
  }

  if (currentContent instanceof HTMLElement && currentPanel && !currentPanel.hasAttribute('hidden')) {
    currentContent.classList.add('cart-modal__tabpanel-content--fade-out');
    currentContent.style.opacity = '0';

    const onFadeOutEnd = () => {
      currentContent.removeEventListener('transitionend', onFadeOutEnd);
      finishSwitch();
      fadeInNewPanel();
    };

    currentContent.addEventListener('transitionend', onFadeOutEnd, { once: true });
    setTimeout(() => {
      if (currentContent.classList.contains('cart-modal__tabpanel-content--fade-out')) {
        currentContent.removeEventListener('transitionend', onFadeOutEnd);
        finishSwitch();
        fadeInNewPanel();
      }
    }, TAB_FADE_DURATION_MS + 50);
  } else {
    finishSwitch();
    fadeInNewPanel();
  }
};

/**
 * Document-level handler so tabs work even if the custom element did not upgrade.
 */
const handleCartUpsellTabClick = (event) => {
  if (!(event.target instanceof Element)) return;

  const tab = event.target.closest('cart-upsell-tabs .cart-modal__tab');
  if (!(tab instanceof HTMLElement)) return;

  const root = tab.closest('cart-upsell-tabs');
  if (!(root instanceof HTMLElement)) return;

  event.preventDefault();
  event.stopPropagation();
  root.dataset.userSelectedTab = 'true';
  switchUpsellTab(root, tab, true);
};

if (!window.__cartUpsellTabsDocumentClickBound) {
  window.__cartUpsellTabsDocumentClickBound = true;
  document.addEventListener('click', handleCartUpsellTabClick, true);
}

/**
 * Standalone upsell tabs for the cart page (same UI + tab animation as cart modal).
 */
class CartUpsellTabs extends HTMLElement {
  #attachedQuantitySwitchers = new WeakSet();

  connectedCallback() {
    this.upsellSection = this.querySelector('.cart-modal__upsell');
    if (!this.upsellSection) return;

    this.#bindUpsellInteractions();
    this.#initializeQuantitySwitcher();
    this.#refreshTabIndicator();
    initCartUpsellCarousels(this);
  }

  #bindUpsellInteractions() {
    if (this.dataset.upsellInteractionsBound === 'true') return;
    this.dataset.upsellInteractionsBound = 'true';

    this.addEventListener('click', this.#handleUpsellAddToCartClick, true);
    this.addEventListener('submit', this.#handleUpsellFormSubmit, true);
    this.#bindQuantitySelects();
  }

  #handleUpsellFormSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('cart-modal-upsell-card__form')) {
      return;
    }
    if (!this.contains(form)) return;

    event.preventDefault();
    event.stopPropagation();
  };

  #handleUpsellAddToCartClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('.cart-modal-upsell-card__button');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    event.preventDefault();
    event.stopPropagation();

    const form = button.closest('form');
    const card = button.closest('.cart-modal-upsell-card');
    if (!form || !card) return;

    const variantIdInput = form.querySelector('input[name="id"]');
    const quantitySelect = card.querySelector('select[data-cart-modal-quantity]');
    const quantityNumberInput = card.querySelector('input[type="number"][data-cart-modal-quantity]');
    const quantityInput = form.querySelector('input[name="quantity"]');

    if (!(variantIdInput instanceof HTMLInputElement)) return;

    const variantId = parseInt(variantIdInput.value, 10);
    let quantity = 1;

    if (quantitySelect instanceof HTMLSelectElement) {
      quantity = parseInt(quantitySelect.value, 10) || 1;
    } else if (quantityNumberInput instanceof HTMLInputElement) {
      quantity = parseInt(quantityNumberInput.value, 10) || 1;
    } else if (quantityInput instanceof HTMLInputElement) {
      quantity = parseInt(quantityInput.value, 10) || 1;
    }

    if (!variantId || Number.isNaN(variantId) || quantity <= 0) return;

    this.#showAddToCartLoading(button);
    await this.#waitForPaint();

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity, properties: { _added_from: 'CartUpsell' } }] }),
      });

      const result = await response.json();
      if (!response.ok || (result.status && result.status >= 400)) {
        throw new Error(result.message || result.description || 'Failed to add item to cart');
      }

      this.#showAddToCartSuccess(button);

      document.dispatchEvent(
        new CustomEvent(CART_UPDATE_EVENT, {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: String(variantId),
            data: {
              productId: card.dataset.productId,
              itemCount: quantity,
              source: 'cart-upsell-tabs',
            },
          },
        })
      );
    } catch (error) {
      this.#clearAddToCartLoading(button);
      console.error('Error adding upsell product to cart:', error);
    }
  };

  #showAddToCartLoading(button) {
    const panelContent = button.closest('.cart-modal__tabpanel-content');
    if (panelContent instanceof HTMLElement) {
      panelContent.classList.remove('cart-modal__tabpanel-content--fade-out');
      panelContent.style.opacity = '1';
    }

    button.setAttribute('data-atc-state', 'loading');
    button.disabled = true;
  }

  #showAddToCartSuccess(button) {
    button.removeAttribute('data-atc-state');
    button.classList.add('atc-added');
    button.disabled = false;

    setTimeout(() => button.classList.remove('atc-added'), ADD_TO_CART_ANIMATION_MS);
  }

  #clearAddToCartLoading(button) {
    button.removeAttribute('data-atc-state');
    button.disabled = false;
  }

  #waitForPaint() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
  }

  #bindQuantitySelects() {
    if (!this.upsellSection) return;

    const quantitySelects = this.upsellSection.querySelectorAll('select[data-cart-modal-quantity]');
    for (const select of quantitySelects) {
      if (!(select instanceof HTMLSelectElement)) continue;
      if (select.dataset.cartUpsellBound === 'true') continue;
      select.dataset.cartUpsellBound = 'true';
      select.addEventListener('change', () => {
        const quantityInput = select
          .closest('.cart-modal-upsell-card')
          ?.querySelector('form input[name="quantity"]');
        if (quantityInput instanceof HTMLInputElement) quantityInput.value = select.value;
      });
    }
  }

  #refreshTabIndicator() {
    const selectedTab = this.querySelector('.cart-modal__tab[aria-selected="true"]');
    if (selectedTab instanceof HTMLElement) {
      requestAnimationFrame(() => updateUpsellTabIndicator(this, selectedTab));
    }
  }

  #initializeQuantitySwitcher() {
    if (!this.upsellSection) return;

    const containers = this.upsellSection.querySelectorAll(
      '[data-quantity-switcher] select[data-cart-modal-quantity]'
    );
    for (const selectEl of containers) {
      if (!(selectEl instanceof HTMLSelectElement)) continue;
      if (this.#attachedQuantitySwitchers.has(selectEl)) continue;
      this.#attachedQuantitySwitchers.add(selectEl);

      selectEl.addEventListener('change', (e) => {
        const target = e.target;
        if (!(target instanceof HTMLSelectElement)) return;
        if (target.value !== '11') return;

        const wrapper = target.closest('[data-quantity-switcher]');
        if (!wrapper) return;

        const input = document.createElement('input');
        input.type = 'number';
        input.name = 'quantity';
        input.value = '11';
        input.className = 'cart-modal-upsell-card__quantity';
        input.setAttribute('data-cart-modal-quantity', 'true');
        input.min = '1';
        wrapper.innerHTML = '';
        wrapper.appendChild(input);

        const card = wrapper.closest('.cart-modal-upsell-card');
        const quantityInput = card?.querySelector('form input[name="quantity"]');
        if (quantityInput instanceof HTMLInputElement) {
          quantityInput.value = input.value;
        }

        input.addEventListener('change', () => {
          if (quantityInput instanceof HTMLInputElement) quantityInput.value = input.value;
        });
      });
    }
  }

}

if (!customElements.get('cart-upsell-tabs')) {
  customElements.define('cart-upsell-tabs', CartUpsellTabs);
}

/**
 * Upgrade any cart-upsell-tabs already in the DOM when this script loads.
 */
const upgradeExistingCartUpsellTabs = () => {
  for (const el of document.querySelectorAll('cart-upsell-tabs')) {
    if (el instanceof CartUpsellTabs) {
      el.connectedCallback();
    }
  }

  initCartUpsellCarousels(document);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', upgradeExistingCartUpsellTabs);
} else {
  upgradeExistingCartUpsellTabs();
}
